import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Button,
  TablePagination,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButtonGroup, ToggleButton, Grid, Tooltip, LinearProgress,
  Collapse, IconButton, TextField, Alert,
} from '@mui/material'
import {
  Refresh, VerifiedUser, TrendingUp, TrendingDown, TrendingFlat,
  ExpandMore, ExpandLess, Speed, Warning, ArrowUpward,
} from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { analyticsApi } from '../services/api'
import { RootState } from '../store'
import DriftBadge from '../components/DriftBadge'
import CertificateGuide from '../components/CertificateGuide'
import BundleTeaser from '../components/BundleTeaser'
import CertificateSpecimen from '../components/CertificateSpecimen'
import { BUNDLE_COPY } from '../components/bundleCopy'
import useCanContactVendor from '../hooks/useCanContactVendor'
import BundleBadge from '../components/BundleBadge'

const CertificatesPage = () => {
  const navigate = useNavigate()
  const [certificates, setCertificates] = useState<any[]>([])
  // Refused for want of an entitlement, which is a different thing from
  // empty and must never render as it.
  const [refused, setRefused] = useState(false)
  // Separate from `refused`: certificates and attestation are two keys in
  // the same bundle, so a partial grant can leave one readable and the
  // other not.
  const [refusedAttestation, setRefusedAttestation] = useState(false)
  const canContactVendor = useCanContactVendor()
  const [driftSummary, setDriftSummary] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  // One control, two kinds of filter. `active`/`suspended`/`expired` are
  // certificate states the server can filter on; `needs_review` is not a
  // state at all — it is the set behind the summary tile, which only this
  // page can compute because it owns both the certificate list and the
  // drift summary. Keeping them in one exclusive control is deliberate: they
  // are alternative answers to "which of these do I look at", and two
  // separate controls would let a reader combine them into an empty table
  // and read it as "none".
  const [view, setView] = useState<string | null>(null)
  const statusFilter = view === 'needs_review' ? null : view
  const reviewOnly = view === 'needs_review'
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedCert, setSelectedCert] = useState<any>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null)
  // B1 — renewal now requires a reason and is audited, so it has to be
  // collected rather than sent blank. It clears suspension, rotates the
  // baseline, resets the verdict and resets the 90-day clock.
  const [renewTarget, setRenewTarget] = useState<string | null>(null)
  const [renewReason, setRenewReason] = useState('')
  const [renewError, setRenewError] = useState('')
  const [renewing, setRenewing] = useState(false)
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id

  useEffect(() => { if (tenantId) fetchAll() }, [tenantId, statusFilter])
  // Changing the filter changes the set; staying on page 4 of the old one
  // shows an empty table and reads as "no results". Keyed on `view`, not
  // `statusFilter`, because switching to `needs_review` narrows the set
  // without changing what was requested from the server.
  useEffect(() => { setPage(0) }, [view])

  const fetchAll = async () => {
    setLoading(true)
    try {
      // Page through the whole set rather than taking the endpoint's default.
      //
      // This passed no limit at all, so it got `Query(25, le=100)` and
      // rendered 25 certificates out of 198 with nothing to click for the
      // rest — the remaining 173 were unreachable from the UI. The ceiling is
      // 100, so a single larger request cannot cover a real tenant either;
      // paging is the only correct answer.
      //
      // Same shape as the agent registry, which pulls every page for the same
      // reason: the table is filtered and sorted client-side, so a partial
      // set does not merely truncate the view, it makes the filters wrong.
      const params = statusFilter ? { status: statusFilter } : {}
      const PAGE_SIZE = 100
      const collect = async () => {
        const all: any[] = []
        let total = Infinity
        while (all.length < total) {
          const res = await analyticsApi.getTenantCertificates(tenantId!, {
            ...params, limit: PAGE_SIZE, offset: all.length,
          })
          const page = res.data?.certificates || []
          total = res.data?.total ?? page.length
          all.push(...page)
          // An empty page with more claimed would otherwise spin forever.
          if (page.length === 0) break
        }
        return all
      }
      const [certsRes, summaryRes, attestRes] = await Promise.allSettled([
        collect(),
        analyticsApi.getDriftSummary(tenantId!),
        analyticsApi.getAttestation(tenantId!),
      ])
      setRefusedAttestation(
        attestRes.status === 'rejected'
        && (attestRes.reason as any)?.response?.status === 404,
      )
      if (certsRes.status === 'fulfilled') {
        setCertificates(certsRes.value || [])
        setRefused(false)
      } else {
        // A 404 here means the tenant does not hold ARIAS Evidence. It is NOT
        // an empty estate, and `allSettled` used to swallow it into the same
        // `[]` as "scanned and found none" — so a tenant with 221 certificates
        // who switched Evidence off was told "No certificates yet", told to
        // run a scan to get some, and told nothing needed configuring. Three
        // false statements about their own data.
        const status = (certsRes.reason as any)?.response?.status
        setRefused(status === 404)
        setCertificates([])
        if (status !== 404) console.error('Failed to fetch certificates:', certsRes.reason)
      }
      if (summaryRes.status === 'fulfilled') setDriftSummary(summaryRes.value.data)
    } catch (e) {
      console.error('Failed to fetch certificates:', e)
    } finally {
      setLoading(false)
    }
  }

  const openRenew = (certId: string) => {
    setRenewTarget(certId)
    setRenewReason('')
    setRenewError('')
  }

  const handleRenew = async () => {
    if (!renewTarget || !renewReason.trim()) return
    setRenewing(true)
    setRenewError('')
    try {
      await analyticsApi.renewCertificate(renewTarget, { reason: renewReason.trim() })
      setRenewTarget(null)
      fetchAll()
    } catch (e: any) {
      // Surfaced rather than swallowed to console: a revoked certificate
      // returns 409 and the user needs to know why nothing happened.
      setRenewError(e?.response?.data?.detail || 'Failed to renew certificate.')
    } finally {
      setRenewing(false)
    }
  }

  const handleViewDetail = async (certId: string) => {
    try {
      const res = await analyticsApi.getCertificate(certId)
      setSelectedCert(res.data)
      setDetailOpen(true)
      setExpandedEvent(null)
    } catch (e) {
      console.error('Failed to fetch certificate detail:', e)
    }
  }

  const statusColor = (status: string) => {
    const map: Record<string, any> = { active: 'success', suspended: 'warning', expired: 'default', revoked: 'error' }
    return map[status] || 'default'
  }

  const trendIcon = (trend: string) => {
    if (trend === 'accelerating') return <TrendingUp sx={{ color: '#f44336', fontSize: 18 }} />
    if (trend === 'decelerating') return <TrendingDown sx={{ color: '#4caf50', fontSize: 18 }} />
    return <TrendingFlat sx={{ color: '#9e9e9e', fontSize: 18 }} />
  }

  const trendLabel = (trend: string) => {
    const map: Record<string, string> = {
      accelerating: 'Accelerating', decelerating: 'Decelerating',
      stable: 'Stable', normal: 'Normal', degrading: 'Degrading',
      insufficient_data: 'New', no_data: 'No data',
      // Distinct from 'New'. There are drift events, but the agent has not
      // been observed long enough for a recent rate to mean anything against
      // a longer one — reporting a direction here would describe when
      // scanning started, not how the agent is behaving.
      insufficient_history: 'Too early to trend',
    }
    return map[trend] || trend
  }

  const ImprovementBadge = ({ improvement, tooltip }: { improvement?: string; tooltip?: string }) => {
    if (improvement !== 'POSITIVE_DRIFT') return null
    const chip = (
      <Chip
        icon={<ArrowUpward sx={{ fontSize: 14 }} />}
        label="Improved"
        color="success"
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.7rem' }}
      />
    )
    return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip
  }

  // The tile's own population, resolved to rows.
  //
  // The count was previously an assertion with nothing behind it: a reader who
  // doubted "4" had no way to check it, which is how a page ends up being
  // disbelieved rather than read. `agents_at_risk` already carries agent_id,
  // and certificates key on the same stable registry id, so the set is a
  // membership test — no new endpoint and no second definition of "needs
  // review" that could drift from the server's.
  const atRisk: any[] = driftSummary?.agents_at_risk || []
  const atRiskIds = new Set(atRisk.map((a: any) => a.agent_id))
  const visible = reviewOnly
    ? certificates.filter((c: any) => atRiskIds.has(c.agent_id))
    : certificates

  // Composition of the drift-events tile.
  //
  // Absent is not zero. During a rolling deploy this page can be newer than
  // the analytics service, and a missing field rendering as "0 material · 0
  // critical" would state that the fleet is quiet when in fact nothing was
  // asked. `hasBreakdown` keeps the sub-line off until the numbers exist.
  const materialEvents = driftSummary?.fleet_material_events_30d
  const criticalEvents = driftSummary?.fleet_critical_events_30d
  const hasBreakdown = (
    typeof materialEvents === 'number' && typeof criticalEvents === 'number'
  )
  const totalEvents = driftSummary?.fleet_drift_events_30d || 0
  // Labelled from the window the server actually applied, not a literal 30.
  // The tenant window is configurable (7-90) and the field names keep saying
  // 30d for back-compat, so a hardcoded label is a claim this page is in no
  // position to make. Falls back to 30 only when the server does not say.
  const windowDays = driftSummary?.window_days || 30
  // Cosmetic is the remainder, not a reported field — deriving it here keeps
  // one definition of the whole rather than inventing a second.
  const cosmeticEvents = hasBreakdown
    ? Math.max(0, totalEvents - materialEvents - criticalEvents)
    : 0
  const driftEventsBasis = hasBreakdown
    ? `Of ${totalEvents} events in the last ${windowDays} days: ${criticalEvents} critical, `
      + `${materialEvents} material, ${cosmeticEvents} cosmetic. `
      + 'Re-baselines and certificate renewals are excluded — in neither case '
      + 'did the agent change.'
    : 'Changes measured against each agent’s certified baseline in the '
      + `last ${windowDays} days. Re-baselines and certificate renewals are `
      + 'excluded — in neither case did the agent change.'

  // Why each agent is in the review set, counted. A number a reader cannot
  // decompose is a number they can only believe or disbelieve.
  const reviewReasons = [
    [atRisk.filter((a: any) => (a.drift_stats?.critical_events_30d || 0) > 0).length,
      'had a critical drift event'],
    [atRisk.filter((a: any) => (a.drift_stats?.drift_velocity_30d || 0) >= 3).length,
      'changed 3+ times'],
    [atRisk.filter((a: any) => a.drift_stats?.trend === 'accelerating').length,
      'are changing faster than before'],
    [atRisk.filter((a: any) => a.status === 'expired').length,
      'have an expired certificate'],
  ] as [number, string][]
  const reviewBasis = atRisk.length === 0
    ? `No agent changed materially in the last ${windowDays} days and no `
      + 'certificate has expired. This is about change since certification — '
      + 'for what is wrong today, see Findings.'
    : reviewReasons.filter(([n]) => n > 0).map(([n, why]) => `${n} ${why}`).join('; ')
      + '. An agent can be counted for more than one reason. This is about '
      + 'change since certification, not how many issues an agent has — for '
      + 'that, see Findings.'

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="span">Agent Certificates</Typography>
          <BundleBadge bundle="ARIAS Evidence" refused={refused} />
          <Typography variant="body2" color="text.secondary">
            Know when an agent starts behaving differently from the version you approved
          </Typography>
          {/* A customer read four here beside hundreds of critical and high
              findings and asked which was true. Both were — these pages
              measure different things and neither said so.

              A link, not a paragraph: the reader who is confused wants the
              other page, and routing them there is worth more than explaining
              the distinction at them. */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Certificates answer <em>what changed since we certified this agent</em>.
            For <em>what is wrong today</em>, see{' '}
            <Box
              component="span"
              onClick={() => navigate('/analyses')}
              sx={{
                color: 'primary.main', cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Findings
            </Box>.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAll}>
          Refresh
        </Button>
      </Box>

      <CertificateGuide />

      {/* Fleet Drift Summary */}
      {driftSummary && driftSummary.total_agents > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4">{driftSummary.total_agents}</Typography>
                <Typography variant="caption" color="text.secondary">Agents Tracked</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            {/* The total alone carried no decision. Twelve events might be
                twelve renamed variables or twelve agents that gained a tool,
                and a reader had no way to tell which.

                Material and critical are shown INSIDE this tile rather than
                as tiles of their own because they are a decomposition of this
                number, not separate magnitudes. Side by side, a reader would
                be invited to add them, or to take material for a different
                population. Cosmetic is left to the tooltip: naming it here
                would give the ignore-this bucket equal billing with the two
                that matter. */}
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Tooltip title={driftEventsBasis}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h4">{driftSummary.fleet_drift_events_30d}</Typography>
                      {trendIcon(driftSummary.fleet_trend)}
                    </Box>
                    <Typography variant="caption" color="text.secondary">Drift events ({windowDays}d)</Typography>
                    {hasBreakdown && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                      <Box component="span" color="text.secondary">
                        {materialEvents} material
                      </Box>
                      <Box component="span" color="text.secondary"> · </Box>
                      {/* The only red on the page, and it marks something
                          specific. Red on "agents at risk" competed with the
                          severity colours on Findings for a different meaning;
                          a critical drift event is defensibly red. */}
                      <Box
                        component="span"
                        color={criticalEvents > 0 ? 'error.main' : 'text.secondary'}
                        sx={{ fontWeight: criticalEvents > 0 ? 600 : 400 }}
                      >
                        {criticalEvents} critical
                      </Box>
                    </Typography>
                    )}
                  </Box>
                </Tooltip>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" color={driftSummary.suspended_certs > 0 ? 'warning.main' : 'text.primary'}>
                  {driftSummary.suspended_certs}
                </Typography>
                <Typography variant="caption" color="text.secondary">Suspended</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            {/* "Agents at Risk" was the wrong name and it put this page in
                open conflict with Findings. A customer saw 4 here beside 193
                critical and 726 high findings across 108 agents and asked
                which was true — both were, because they answer different
                questions and only one of them said which.

                This counts CHANGE and lapsed assurance: drifted materially in
                the last 30 days, or a certificate that is no longer valid. An
                agent with five criticals and no drift is not counted, and that
                is correct — it was misconfigured on day one and has not moved.

                Amber, not red. A material change is worth a look; it is not by
                itself a failure, and red here competed with the severity
                colours on Findings for a different meaning.

                Clickable, because a number nobody can open is a number that
                can only be believed or disbelieved. The denominator is there
                for the same reason: 4 in isolation reads as a verdict on the
                estate, 4 of 221 reads as a shortlist. */}
            <Card
              onClick={() => setView(reviewOnly ? null : 'needs_review')}
              sx={{
                cursor: 'pointer',
                outline: reviewOnly ? '2px solid' : 'none',
                outlineColor: 'warning.main',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Tooltip title={reviewBasis}>
                  <Box>
                    <Typography variant="h4" color={atRisk.length > 0 ? 'warning.main' : 'success.main'}>
                      {atRisk.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Need review ({windowDays}d)
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                      of {driftSummary.total_agents} certified
                    </Typography>
                  </Box>
                </Tooltip>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => setView(v)}
          size="small"
        >
          <ToggleButton value={null as any}>All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="suspended">Suspended</ToggleButton>
          <ToggleButton value="expired">Expired</ToggleButton>
          {/* Only offered once the summary that defines it has loaded.
              Without it the filter would match nothing and render as "no
              certificates", which is a false statement about the estate
              rather than a missing answer. */}
          {driftSummary && driftSummary.total_agents > 0 && (
            <ToggleButton value="needs_review">
              Needs review ({atRisk.length})
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Box>

      {refusedAttestation && !refused && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <BundleTeaser
              copy={BUNDLE_COPY.attestation}
              canContactVendor={canContactVendor}
              rows={3}
              compact
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          {refused ? (
            <BundleTeaser
              copy={BUNDLE_COPY.certificates}
              canContactVendor={canContactVendor}
              // The content IS the pitch here, so a specimen replaces the
              // blur. Blurring the fields hides the only part that explains
              // why a certificate is worth having.
              specimen={<CertificateSpecimen />}
            />
          ) : visible.length === 0 && certificates.length > 0 ? (
            // A filter that matched nothing is not an estate with nothing in
            // it. Telling a tenant with 221 certificates to "run a scan"
            // because their filter is narrow is three false statements at
            // once, and this page has made that mistake before.
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <VerifiedUser sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                {reviewOnly ? 'Nothing needs review' : 'No certificates match this filter'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mt: 1 }}>
                {reviewOnly
                  ? `No agent changed materially in the last ${windowDays} days and no `
                    + 'certificate has expired. This says nothing about issues an agent already had '
                    + 'when it was certified — for those, see Findings.'
                  : `${certificates.length} certificates are present under other filters.`}
              </Typography>
              <Button size="small" sx={{ mt: 1.5 }} onClick={() => setView(null)}>
                Show all certificates
              </Button>
            </Box>
          ) : certificates.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <VerifiedUser sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">No certificates yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mt: 1 }}>
                Run a scan and ARIAS issues one certificate per agent it finds, recording
                how that agent behaves today. From then on every scan is compared against
                that record, so you hear about it when an agent gains a tool, changes
                model, or starts reaching data it did not reach before.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mt: 1.5 }}>
                Nothing to configure &mdash; certificates are issued automatically.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Agent Name</TableCell>
                    <TableCell>Repository</TableCell>
                    <TableCell>Framework</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Latest Drift</TableCell>
                    <TableCell>Drift ({windowDays}d)</TableCell>
                    <TableCell>Trend</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c: any) => {
                    const stats = c.drift_stats || {}
                    return (
                      <TableRow key={c.id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => handleViewDetail(c.id)}>
                        <TableCell>
                          <Typography variant="subtitle2">{c.agent_name || c.agent_id?.substring(0, 12)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{c.repository_name || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={c.framework || 'unknown'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={c.status} color={statusColor(c.status)} size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <DriftBadge verdict={c.latest_drift_verdict} />
                            <ImprovementBadge
                              improvement={c.latest_improvement}
                              tooltip="Governance posture improved in the last scan"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={`${stats.material_events_30d || 0} material, ${stats.critical_events_30d || 0} critical, ${stats.improvement_events_30d || 0} improved`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Speed sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {stats.drift_velocity_30d || 0}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {trendIcon(stats.trend || 'stable')}
                            <Typography variant="caption">{trendLabel(stats.trend || 'stable')}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => { e.stopPropagation(); openRenew(c.id) }}
                            disabled={c.status === 'active' && c.latest_drift_verdict === 'STABLE'}
                          >
                            Renew
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={visible.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[25, 50, 100, 200]}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Certificate Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Certificate: {selectedCert?.agent_name || selectedCert?.agent_id?.substring(0, 12)}
        </DialogTitle>
        <DialogContent>
          {selectedCert && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label={selectedCert.status} color={statusColor(selectedCert.status)} />
                <Chip label={selectedCert.framework || 'unknown'} variant="outlined" />
                <DriftBadge verdict={selectedCert.latest_drift_verdict} size="medium" />
                <ImprovementBadge
                  improvement={selectedCert.latest_improvement}
                  tooltip="Governance posture improved in the most recent scan"
                />
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Issued:</strong> {selectedCert.issued_at ? new Date(selectedCert.issued_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Expires:</strong> {selectedCert.expires_at ? new Date(selectedCert.expires_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
                {selectedCert.suspended_at && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="warning.main">
                      <strong>Suspended:</strong> {new Date(selectedCert.suspended_at).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Drift Stats */}
              {selectedCert.drift_stats && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" gutterBottom>Drift Activity ({windowDays} days)</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="h5">{selectedCert.drift_stats.drift_velocity_30d}</Typography>
                        <Typography variant="caption" color="text.secondary">Drift events</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="h5" color="success.main">
                          {selectedCert.drift_stats.improvement_events_30d || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Improvements</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="h5">{selectedCert.drift_stats.cumulative_drift_30d}</Typography>
                        <Typography variant="caption" color="text.secondary">Cumulative score</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {trendIcon(selectedCert.drift_stats.trend)}
                          <Typography variant="h6">{trendLabel(selectedCert.drift_stats.trend)}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">Trend</Typography>
                      </Grid>
                    </Grid>
                    {selectedCert.drift_stats.stability_streak > 0 && (
                      <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                        {selectedCert.drift_stats.stability_streak} consecutive stable scan{selectedCert.drift_stats.stability_streak > 1 ? 's' : ''}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Drift Events with Component Details */}
              {selectedCert.drift_events?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Drift History</Typography>
                  {selectedCert.drift_events.map((e: any) => {
                    const changedComponents = e.component_drifts
                      ? Object.entries(e.component_drifts).filter(([k, v]: any) => v?.changed && !k.startsWith('_'))
                      : []
                    return (
                    <Card key={e.id} variant="outlined" sx={{ mb: 1 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, cursor: 'pointer' }}
                        onClick={() => setExpandedEvent(expandedEvent === e.id ? null : e.id)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <DriftBadge verdict={e.verdict} />
                          <ImprovementBadge improvement={e.improvement} tooltip="Governance posture improved in this scan" />
                          <Typography variant="caption" color="text.secondary">
                            {e.drift_score?.toFixed(2)}
                          </Typography>
                          {changedComponents.map(([name]: any) => (
                            <Chip key={name} label={name.replace(/_/g, ' ')} size="small" variant="outlined"
                              color="warning" sx={{ fontSize: '0.65rem', height: 20, textTransform: 'capitalize' }} />
                          ))}
                          <Typography variant="caption" color="text.disabled">
                            {new Date(e.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                        <IconButton size="small">
                          {expandedEvent === e.id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                      {e.summary && (
                        <Typography variant="body2" sx={{ px: 2, pb: 1 }} color="text.secondary">
                          {e.summary}
                        </Typography>
                      )}
                      <Collapse in={expandedEvent === e.id}>
                        <Box sx={{ px: 2, pb: 2 }}>
                          {e.component_drifts && <ComponentDriftDetail drifts={e.component_drifts} />}
                          {e.change_impact_recommendations?.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>Change Impact Analysis</Typography>
                              {e.change_impact_recommendations.map((rec: any, i: number) => (
                                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1, p: 1, bgcolor: 'info.light', borderRadius: 1, opacity: 0.9 }}>
                                  <Chip label={rec.priority} size="small"
                                    color={rec.priority === 'high' ? 'error' : 'warning'}
                                    sx={{ fontSize: '0.6rem', height: 18 }} />
                                  <Typography variant="caption">{rec.test}</Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                          {e.change_impact_preview && !e.change_impact_recommendations && (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, border: '1px dashed', borderColor: 'grey.400' }}>
                              <Typography variant="caption" color="text.secondary">
                                {e.change_impact_preview.recommendations_count} test recommendation{e.change_impact_preview.recommendations_count > 1 ? 's' : ''} available
                              </Typography>
                              {e.change_impact_preview.upgrade_message && (
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  <a href="/pricing">{e.change_impact_preview.upgrade_message}</a>
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Card>
                    )
                  })}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
          {selectedCert && (
            <>
              {/* P1b — open full drift timeline + approve action. */}
              <Button
                onClick={() => { setDetailOpen(false); navigate(`/certificates/${selectedCert.id}`) }}
              >
                Open timeline
              </Button>
              <Button
                variant="contained"
                onClick={() => { openRenew(selectedCert.id); setDetailOpen(false) }}
                disabled={selectedCert.status === 'active' && selectedCert.latest_drift_verdict === 'STABLE'}
              >
                Renew Certificate
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* B1 — renewal is audited and requires a reason, so it is collected
          here rather than sent blank. Renew is the most powerful of the three
          certificate actions: it clears suspension, rotates the baseline,
          resets the drift verdict and restarts the 90-day clock. */}
      <Dialog open={!!renewTarget} onClose={() => setRenewTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Renew certificate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This re-attests the agent's current behaviour as its new baseline and
            restarts the 90-day validity period. The reason is recorded in the
            audit trail alongside your name.
          </Typography>
          {renewError && <Alert severity="error" sx={{ mb: 2 }}>{renewError}</Alert>}
          <TextField
            label="Reason"
            placeholder="e.g. Reviewed the added tool with the owning team; change is intended."
            fullWidth
            multiline
            minRows={2}
            value={renewReason}
            onChange={(e) => setRenewReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!renewReason.trim() || renewing}
            onClick={handleRenew}
          >
            {renewing ? 'Renewing…' : 'Renew certificate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}


const ComponentDriftDetail = ({ drifts }: { drifts: Record<string, any> }) => {
  const components = Object.entries(drifts).filter(([_, v]) => v?.changed)
  if (components.length === 0) return <Typography variant="caption" color="text.secondary">No component changes recorded</Typography>

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>Changed Components</Typography>
      {components.map(([name, detail]) => (
        <Box key={name} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: detail.is_critical ? 'error.main' : 'grey.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              label={name.replace(/_/g, ' ')}
              size="small"
              color={detail.is_critical ? 'error' : 'warning'}
              variant="outlined"
              sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '0.7rem' }}
            />
            {detail.is_critical && (
              <Chip label="CRITICAL" size="small" color="error" sx={{ fontSize: '0.6rem', height: 18 }} />
            )}
          </Box>

          {/* Tools drift */}
          {name === 'tools' && (
            <Box>
              {detail.added?.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>Added:</strong>{' '}
                  {detail.added.map((t: any) => `${t.name} (${t.type}${t.domain === 'external' ? ', external' : ''})`).join(', ')}
                </Typography>
              )}
              {detail.removed?.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>Removed:</strong> {detail.removed.map((t: any) => t.name).join(', ')}
                </Typography>
              )}
              {detail.new_high_risk_types?.length > 0 && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                  <Warning sx={{ fontSize: 12, mr: 0.5 }} />
                  New high-risk tool types: {detail.new_high_risk_types.join(', ')}
                </Typography>
              )}
            </Box>
          )}

          {/* Memory drift */}
          {name === 'memory' && (
            <Box>
              {detail.became_cross_agent_readable && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                  <Warning sx={{ fontSize: 12, mr: 0.5 }} />
                  Memory became cross-agent readable
                </Typography>
              )}
              {detail.new_read_sources?.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>New read sources:</strong> {detail.new_read_sources.join(', ')}
                </Typography>
              )}
              {detail.new_write_targets?.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>New write targets:</strong> {detail.new_write_targets.join(', ')}
                </Typography>
              )}
            </Box>
          )}

          {/* Orchestration drift */}
          {name === 'orchestration' && (
            <Box>
              {detail.hitl_removed && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                  <Warning sx={{ fontSize: 12, mr: 0.5 }} />
                  Human-in-the-loop removed
                </Typography>
              )}
              {detail.depth_increased && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                  Orchestration depth increased
                </Typography>
              )}
              {detail.new_outbound_calls?.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>New outbound calls:</strong> {detail.new_outbound_calls.join(', ')}
                </Typography>
              )}
            </Box>
          )}

          {/* Goal drift */}
          {name === 'goal' && (
            <Box>
              {detail.previous_preview && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>Previous:</strong> {detail.previous_preview}
                </Typography>
              )}
              {detail.current_preview && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>Current:</strong> {detail.current_preview}
                </Typography>
              )}
            </Box>
          )}

          {/* Error posture drift */}
          {name === 'error_posture' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption">
                {detail.previous_score} &rarr; {detail.current_score}
              </Typography>
              <Chip
                label={`${detail.delta > 0 ? '+' : ''}${detail.delta}`}
                size="small"
                color={detail.delta > 0 ? 'success' : 'error'}
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  )
}

export default CertificatesPage
